import React from "react"
import { useIntl } from "react-intl"
import { useQueryClient } from "@tanstack/react-query"
import { PageLayout } from "#/features/global-ui/page-layout"
import { PageHeader } from "#/features/global-ui/page-header"
import { SUBSCRIPTION_STATUS_QUERY_KEY } from "#/services/user/hooks/useSubscriptionStatus"
import { Box, Progress } from "@carrotfertility/carotene-core"

// The consumer package is code-split and lazy-loaded (mirroring Root.tsx), so none of the
// package's billing code loads until a signed-in member navigates to this page. React.lazy
// infers the route's prop types from the dynamic import.
const ConsumerSubscriptionBillingRoute = React.lazy(() =>
  import("@internal/consumer").then((m) => ({ default: m.ConsumerSubscriptionBillingRoute }))
)

export default function ConsumerSubscriptionBillingPage(): JSX.Element {
  const intl = useIntl()
  const queryClient = useQueryClient()

  // This is the page a lapsed member pays their past-due invoice on, and the consumer package
  // keeps that outcome in its own cache — it invalidates its billing summary and card, not the
  // host's paywall verdict, which would otherwise hold `lapsed` for the rest of the session and
  // wall every route until a full page reload. Invalidated on the way out rather than on the
  // payment itself because the payment belongs to the package: leaving this page is the coarsest
  // signal the host owns, and it costs one request.
  React.useEffect(() => {
    return () => {
      queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_STATUS_QUERY_KEY })
    }
  }, [queryClient])

  return (
    <PageLayout header={<PageHeader pageTitle={intl.formatMessage({ defaultMessage: "Billing detail" })} />}>
      <React.Suspense
        fallback={
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              blockSize: 200
            }}
          >
            <Progress />
          </Box>
        }
      >
        {/* This host page only mounts for authenticated members (routed inside the authed
            NavLayout chrome), so `isLoggedIn` is always true here. */}
        <ConsumerSubscriptionBillingRoute isLoggedIn={true} />
      </React.Suspense>
    </PageLayout>
  )
}
